/**
 * Auth client.
 *
 * Web : deux cookies httpOnly + Secure + SameSite=Strict posés par l'API, aucun
 * jeton lisible en JS : session (jeton d'accès, Path=/) et rafraîchissement
 * (Path=/api/auth). Le corps des réponses ne contient aucun jeton.
 * Mobile (Capacitor) : `client: 'native'` au login ; jeton d'accès en mémoire
 * JS, jeton de rafraîchissement en Secure Storage natif, envoyé dans le corps.
 *
 *   POST /api/auth/login    web    -> Set-Cookie session + refresh, { user }
 *                           native -> { access_token, refresh_token, user }
 *   POST /api/auth/refresh  web    {}                 -> Set-Cookie session + refresh
 *                           native { refresh_token }  -> { access_token, refresh_token }
 *   POST /api/auth/logout          -> 204 (efface les deux cookies)
 *   GET  /api/me                   -> profil utilisateur
 */
import { isNative } from './platform';
import { secureStorage } from './secureStorage';

const REFRESH_KEY = 'jx.refresh';
let accessTokenMemory: string | null = null;
let refreshInFlight: Promise<void> | null = null;

/**
 * Fin de session constatée côté client : un 401 dont le rafraîchissement est
 * REFUSÉ (session V1 expirée ou fermée, compte verrouillé…). AuthContext s'y
 * abonne pour purger l'utilisateur, ce qui renvoie la page protégée vers
 * /login. Rien n'est émis pour une erreur passagère (429, 5xx, réseau).
 */
type SessionExpiredListener = () => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function onSessionExpired(fn: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(fn);
  return () => {
    sessionExpiredListeners.delete(fn);
  };
}

/**
 * Époque de session : change à chaque connexion et déconnexion. Une requête
 * partie sous une époque n'annonce pas la fin de la session suivante (réponse
 * tardive d'avant une nouvelle connexion).
 */
let sessionEpoch = 0;

function notifySessionExpired(): void {
  for (const fn of [...sessionExpiredListeners]) {
    try {
      fn();
    } catch {
      /* un abonné défaillant n'empêche pas les autres */
    }
  }
}

type LoginResp = { access_token: string; refresh_token?: string };

/**
 * Refus de la connexion par mot de passe d'un agent Digital Team quand la
 * connexion DigitalTeam (SSO) est obligatoire (FEATURE_SSO_REQUIRED_DT) :
 * la page affiche le message et met en avant le bouton SSO.
 */
export class SsoRequiredError extends Error {
  constructor(public readonly ssoUrl: string) {
    super('Connexion DigitalTeam obligatoire');
    this.name = 'SsoRequiredError';
  }
}

export async function login(login: string, password: string, mfaCode?: string): Promise<void> {
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login, password, mfa: mfaCode, ...(isNative() ? { client: 'native' } : {}) }),
  });
  if (!res.ok) {
    const status = res.status;
    if (status === 401) throw new Error('Identifiants invalides ou compte bloqué');
    if (status === 403) {
      const body = (await res.json().catch(() => null)) as { sso_required?: boolean; sso_url?: string } | null;
      if (body?.sso_required) {
        // Lien relatif seulement (jamais une URL absolue venue de la réponse).
        const url = typeof body.sso_url === 'string' && body.sso_url.startsWith('/api/auth/oidc/') ? body.sso_url : '/api/auth/oidc/start';
        throw new SsoRequiredError(url);
      }
      throw new Error('Compte verrouillé — réessayez plus tard');
    }
    if (status === 428) throw new Error('Code MFA requis');
    if (status === 429) throw new Error('Trop de tentatives, réessayez dans un instant.');
    if (status >= 500) throw new Error('Service momentanément indisponible, réessayez dans un instant.');
    throw new Error(`Erreur de connexion (${status})`);
  }
  sessionEpoch++;
  if (!isNative()) return; // web : les jetons sont dans les cookies httpOnly
  const data = (await res.json()) as LoginResp;
  accessTokenMemory = data.access_token;
  if (data.refresh_token) await secureStorage.set(REFRESH_KEY, data.refresh_token);
}

export async function logout(): Promise<void> {
  try {
    const init: RequestInit = { method: 'POST', credentials: 'include' };
    if (isNative()) {
      // Le jeton d'accès a pu expirer : l'API retrouve la session par le jeton
      // de rafraîchissement pour la fermer.
      const refresh = await secureStorage.get(REFRESH_KEY).catch(() => null);
      const headers: Record<string, string> = {};
      if (accessTokenMemory) headers.Authorization = `Bearer ${accessTokenMemory}`;
      if (refresh) {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify({ refresh_token: refresh });
      }
      init.headers = headers;
    }
    await fetch('/api/auth/logout', init);
  } finally {
    sessionEpoch++;
    accessTokenMemory = null;
    if (isNative()) await secureStorage.remove(REFRESH_KEY);
  }
}

/** Le rafraîchissement est refusé (session finie) : déconnexion légitime. */
class RefreshRejected extends Error {}

/**
 * Le rafraîchissement n'a pas pu aboutir pour une raison passagère (429, 5xx) :
 * la session n'est pas en cause, on garde les jetons et on rend cette réponse
 * à l'appelant, qui affichera un message temporaire au lieu de déconnecter.
 */
class RefreshUnavailable extends Error {
  constructor(public response: Response) {
    super(`Refresh unavailable (${response.status})`);
  }
}

async function doRefresh(): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  // Web : corps `{}` (un corps JSON vide est refusé par l'API, 400), le jeton
  // part dans le cookie httpOnly de rafraîchissement.
  let body = '{}';
  if (isNative()) {
    const refresh = await secureStorage.get(REFRESH_KEY).catch(() => null);
    if (!refresh) throw new RefreshRejected('No refresh token');
    body = JSON.stringify({ refresh_token: refresh });
  }
  // Une erreur réseau remonte telle quelle (api.ts la traduit en NetworkError).
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers,
    body,
  });
  if (res.status === 429 || res.status >= 500) throw new RefreshUnavailable(res);
  if (!res.ok) {
    accessTokenMemory = null;
    if (isNative()) await secureStorage.remove(REFRESH_KEY);
    throw new RefreshRejected('Refresh failed');
  }
  if (!isNative()) return; // web : nouveaux cookies posés par l'API
  const data = (await res.json()) as LoginResp;
  accessTokenMemory = data.access_token;
  if (data.refresh_token) await secureStorage.set(REFRESH_KEY, data.refresh_token);
}

async function ensureRefreshed(): Promise<void> {
  if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => (refreshInFlight = null));
  return refreshInFlight;
}

/**
 * Fetch authentifié avec retry automatique sur 401 via refresh.
 * Seul un 401 dont le refresh est REFUSÉ est rendu tel quel (déconnexion,
 * annoncée aux abonnés de onSessionExpired) ; un
 * refresh en 429/5xx rend sa propre réponse (erreur passagère, pas de
 * déconnexion), une erreur réseau remonte comme telle.
 * Sur Web le cookie httpOnly est suffisant (credentials: include).
 * Sur Mobile on ajoute Authorization: Bearer <access_token>.
 */
export async function authFetch(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (isNative() && accessTokenMemory) {
    headers.set('Authorization', `Bearer ${accessTokenMemory}`);
  }
  const epoch = sessionEpoch;
  const opts: RequestInit = { ...init, headers, credentials: 'include' };
  let res = await fetch(input, opts);
  if (res.status === 401) {
    try {
      await ensureRefreshed();
    } catch (e) {
      if (e instanceof RefreshRejected) {
        if (epoch === sessionEpoch) notifySessionExpired();
        return res;
      }
      if (e instanceof RefreshUnavailable) return e.response;
      throw e;
    }
    if (isNative() && accessTokenMemory) {
      headers.set('Authorization', `Bearer ${accessTokenMemory}`);
    }
    res = await fetch(input, { ...init, headers, credentials: 'include' });
  }
  return res;
}
